import { useEffect, useId, useRef, useState } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  /** Secondary line under the label, e.g. the scientific name. */
  description?: string;
}

interface Props {
  /** Matches the `htmlFor` of the field's `<label>`, so clicking the label focuses this. */
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  /** Shown when `value` matches no option. */
  placeholder?: string;
  /** Caps the width, in place of the inline `maxWidth` the native selects carried. */
  maxWidth?: string;
}

/**
 * The project's one dropdown for picking from a fixed list.
 *
 * Replaces the native `<select>`, whose popup is browser chrome: it can't be
 * styled, and it rendered differently from the Breed suggestions and the
 * "Who's making the sound" picker sitting beside it in the same forms. This
 * shares its trigger and menu look with both (see `.dropdown-*` in app.css).
 *
 * Follows the ARIA select-only combobox pattern: focus stays on the trigger and
 * the highlighted option is announced through `aria-activedescendant`, so the
 * whole thing works from the keyboard without moving focus into the menu.
 *
 * For free text with suggestions use `Combobox`; for a roster with avatars use
 * `SubjectPicker`. This is for a short closed list.
 */
export function Select({ id, value, onChange, options, placeholder, maxWidth }: Props) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const openMenu = () => {
    setHighlighted(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const choose = (option: SelectOption) => {
    setOpen(false);
    if (option.value !== value) onChange(option.value);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openMenu();
        else setHighlighted((h) => Math.min(h + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openMenu();
        else setHighlighted((h) => Math.max(h - 1, 0));
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setHighlighted(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setHighlighted(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) openMenu();
        else if (options[highlighted]) choose(options[highlighted]!);
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  return (
    <div className="dropdown" ref={rootRef} style={maxWidth ? { maxWidth } : undefined}>
      <button
        id={id}
        type="button"
        role="combobox"
        className="dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open ? `${listboxId}-${highlighted}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className="dropdown-body">
          {selected ? (
            <>
              <span className="dropdown-label">{selected.label}</span>
              {selected.description && (
                <span className="dropdown-description">{selected.description}</span>
              )}
            </>
          ) : (
            <span className="dropdown-placeholder">{placeholder ?? 'Choose…'}</span>
          )}
        </span>
        <span className="dropdown-caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <ul id={listboxId} role="listbox" className="dropdown-menu">
          {options.map((option, i) => (
            <li
              key={option.value}
              id={`${listboxId}-${i}`}
              role="option"
              aria-selected={option.value === value}
              className={[
                'dropdown-option',
                option.value === value ? 'dropdown-option--on' : '',
                i === highlighted ? 'dropdown-option--highlighted' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              // Keeps focus on the trigger, so a click doesn't blur it mid-selection.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => choose(option)}
            >
              <span className="dropdown-label">{option.label}</span>
              {option.description && (
                <span className="dropdown-description">{option.description}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
